import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Package, Truck, Phone, Mail, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ORDER_TIMEZONE = 'Europe/Budapest';

const OrderTracking = () => {
  const { orderNumber: urlOrderNumber } = useParams(); // present on /track-order/:orderNumber
  const [searchType, setSearchType] = useState('email');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [searched, setSearched] = useState(false);
  const [autoLoaded, setAutoLoaded] = useState(false);
  const [pageError, setPageError] = useState('');
  const [configError, setConfigError] = useState('');

  const hasBackendUrl = Boolean(BACKEND_URL);

  const normalizeOrder = (order) => ({
    ...order,
    items: Array.isArray(order?.items) ? order.items : []
  });

  // If arrived via /track-order/:orderNumber, fetch that order directly
  useEffect(() => {
    if (!hasBackendUrl) {
      setConfigError('A rendeléskövetés átmenetileg nem elérhető. Hiányzik a backend beállítás.');
      setLoading(false);
      return;
    }

    if (urlOrderNumber) {
      fetchByOrderNumber(urlOrderNumber);
      return;
    }

    // Otherwise auto-load with saved contact data
    const savedEmail = localStorage.getItem('lastOrderEmail');
    const savedPhone = localStorage.getItem('lastOrderPhone');
    const savedName = localStorage.getItem('lastOrderName');

    if (savedEmail) {
      setSearchType('email');
      setSearchValue(savedEmail);
      setTimeout(() => autoSearch('email', savedEmail), 500);
    } else if (savedPhone) {
      setSearchType('phone');
      setSearchValue(savedPhone);
      setTimeout(() => autoSearch('phone', savedPhone), 500);
    } else if (savedName) {
      setSearchType('name');
      setSearchValue(savedName);
    }
  }, [urlOrderNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Direct lookup by order number (used when coming from /track-order/:orderNumber)
  const fetchByOrderNumber = async (orderNum) => {
    setLoading(true);
    setSearched(true);
    setAutoLoaded(true);
    setPageError('');

    if (!hasBackendUrl) {
      setOrders([]);
      setLoading(false);
      setPageError('Backend kapcsolat nem elérhető.');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/${orderNum}`);
      if (response.ok) {
        const order = await response.json();
        setOrders(order ? [normalizeOrder(order)] : []);
      } else {
        setOrders([]);
        toast.error('A rendelés nem található.');
        setPageError('A rendelés nem található.');
      }
    } catch (error) {
      console.error('Error fetching order by number:', error);
      setOrders([]);
      toast.error('Hiba a rendelés betöltésekor.');
      setPageError('Hiba történt a rendelés betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-search function (for initial load)
  const autoSearch = async (type, value) => {
    setLoading(true);
    setSearched(true);
    setAutoLoaded(true);
    setPageError('');

    if (!hasBackendUrl) {
      setOrders([]);
      setLoading(false);
      setPageError('Backend kapcsolat nem elérhető.');
      return;
    }

    try {
      const params = new URLSearchParams();
      if (type === 'email') params.append('email', value);
      if (type === 'phone') params.append('phone', value);
      if (type === 'name') params.append('name', value);

      const response = await fetch(`${BACKEND_URL}/api/orders/track/search?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        const safeOrders = Array.isArray(data.orders) ? data.orders.map(normalizeOrder) : [];
        setOrders(safeOrders);
        if (safeOrders.length > 0) {
          toast.success(`${safeOrders.length} rendelés automatikusan betöltve`);
        }
      } else {
        setOrders([]);
        setPageError(data.message || 'Nem található rendelés.');
      }
    } catch (error) {
      console.error('Error auto-searching orders:', error);
      setOrders([]);
      setPageError('Hiba történt az automatikus keresés során.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setPageError('');

    if (!hasBackendUrl) {
      setPageError('Backend kapcsolat nem elérhető.');
      return;
    }
    
    if (!searchValue.trim()) {
      toast.error('Kérlek add meg a keresési feltételt!');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const params = new URLSearchParams();
      if (searchType === 'email') params.append('email', searchValue);
      if (searchType === 'phone') params.append('phone', searchValue);
      if (searchType === 'name') params.append('name', searchValue);

      const response = await fetch(`${BACKEND_URL}/api/orders/track/search?${params.toString()}`);
      const data = await response.json();

      // Save search data to localStorage ALWAYS (Premium UX - even if no orders found)
      if (searchType === 'email') {
        localStorage.setItem('lastOrderEmail', searchValue);
        localStorage.removeItem('lastOrderPhone');
        localStorage.removeItem('lastOrderName');
      } else if (searchType === 'phone') {
        localStorage.setItem('lastOrderPhone', searchValue);
        localStorage.removeItem('lastOrderEmail');
        localStorage.removeItem('lastOrderName');
      } else if (searchType === 'name') {
        localStorage.setItem('lastOrderName', searchValue);
        localStorage.removeItem('lastOrderEmail');
        localStorage.removeItem('lastOrderPhone');
      }

      // Handle results
      if (data.success) {
        const safeOrders = Array.isArray(data.orders) ? data.orders.map(normalizeOrder) : [];
        setOrders(safeOrders);
        if (safeOrders.length === 0) {
          toast.info('Nem található rendelés ezzel az adattal');
          setPageError('Nem található rendelés ezzel az adattal.');
        } else {
          toast.success(`${safeOrders.length} rendelés találva`);
        }
      } else {
        setOrders([]);
        toast.error(data.message || 'Nem található rendelés');
        setPageError(data.message || 'Nem található rendelés.');
      }
    } catch (error) {
      console.error('Error searching orders:', error);
      toast.error('Hiba történt a keresés során');
      setOrders([]);
      setPageError('Hiba történt a keresés során.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Új rendelés',
      accepted: 'Elfogadva',
      confirmed: 'Megerősítve',
      preparing: 'Készül',
      ready: 'Kész',
      completed: 'Átadva',
      delivered: 'Kiszállítva',
      cancelled: 'Törölve',
      rejected: 'Visszautasítva'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      accepted: 'bg-blue-100 text-blue-800 border-blue-300',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
      preparing: 'bg-orange-100 text-orange-800 border-orange-300',
      ready: 'bg-green-100 text-green-800 border-green-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
      rejected: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getDeliveryTypeLabel = (type) => {
    const labels = {
      takeaway: 'Elvitel',
      delivery: 'Házhozszállítás',
      dinein: 'Helyben fogyasztás'
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {configError && (
          <div className="mb-6 bg-red-50 border border-red-300 rounded-xl p-4">
            <p className="text-red-800 font-semibold">{configError}</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
            Rendelés Követés
          </h1>
          <p className="text-lg text-gray-600">
            Add meg az email címed, telefonszámod vagy neved a rendelésed kövéséhez
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-8">
          <form onSubmit={handleSearch} className="space-y-6">
            {/* Search Type Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Keresés típusa
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setSearchType('email')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    searchType === 'email'
                      ? 'border-[#00BCD4] bg-[#00BCD4]/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Mail className={`w-6 h-6 mx-auto mb-2 ${searchType === 'email' ? 'text-[#00BCD4]' : 'text-gray-400'}`} />
                  <p className={`text-sm font-semibold ${searchType === 'email' ? 'text-[#00BCD4]' : 'text-gray-600'}`}>
                    Email
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setSearchType('phone')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    searchType === 'phone'
                      ? 'border-[#00BCD4] bg-[#00BCD4]/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Phone className={`w-6 h-6 mx-auto mb-2 ${searchType === 'phone' ? 'text-[#00BCD4]' : 'text-gray-400'}`} />
                  <p className={`text-sm font-semibold ${searchType === 'phone' ? 'text-[#00BCD4]' : 'text-gray-600'}`}>
                    Telefon
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setSearchType('name')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    searchType === 'name'
                      ? 'border-[#00BCD4] bg-[#00BCD4]/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <User className={`w-6 h-6 mx-auto mb-2 ${searchType === 'name' ? 'text-[#00BCD4]' : 'text-gray-400'}`} />
                  <p className={`text-sm font-semibold ${searchType === 'name' ? 'text-[#00BCD4]' : 'text-gray-600'}`}>
                    Név
                  </p>
                </button>
              </div>
            </div>

            {/* Search Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {searchType === 'email' && 'Email cím'}
                {searchType === 'phone' && 'Telefonszám'}
                {searchType === 'name' && 'Név'}
              </label>
              <input
                type={searchType === 'email' ? 'email' : 'text'}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={
                  searchType === 'email'
                    ? 'pelda@email.com'
                    : searchType === 'phone'
                    ? '+36 30 123 4567'
                    : 'Teljes név'
                }
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#00BCD4] focus:outline-none transition-colors text-lg"
              />
            </div>

            {/* Search Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Keresés...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span>Rendelés keresése</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Results */}
        {searched && (
          <div className="space-y-6">
            {pageError && (
              <div className="bg-red-50 border border-red-300 rounded-xl p-4">
                <p className="text-red-800 font-semibold">{pageError}</p>
              </div>
            )}
            {orders.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Nem található rendelés
                </h3>
                <p className="text-gray-600">
                  Nem találtunk rendelést ezzel az adattal. Ellenőrizd, hogy helyesen adtad-e meg!
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-900">
                  Talált rendelések ({orders.length})
                </h2>

                {orders.map((order) => (
                  <div
                    key={order.orderNumber}
                    className="bg-white rounded-2xl shadow-xl overflow-hidden"
                  >
                    {/* Order Header */}
                    <div className="bg-gradient-to-r from-[#00BCD4] to-[#FFA726] p-6">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <h3 className="text-3xl font-black text-white mb-1">
                            #{order.orderNumber}
                          </h3>
                          <p className="text-white/90">
                            {new Date(order.createdAt).toLocaleDateString('hu-HU', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: ORDER_TIMEZONE
                            })}
                          </p>
                        </div>
                        <div className={`px-6 py-3 rounded-xl border-2 font-bold text-lg ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </div>
                      </div>
                    </div>

                    {/* Order Details */}
                    <div className="p-6 space-y-6">
                      {/* Customer Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3 text-gray-700">
                          <User className="w-5 h-5 text-[#00BCD4]" />
                          <div>
                            <p className="text-sm text-gray-500">Név</p>
                            <p className="font-semibold">{order.customerName}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-700">
                          <Phone className="w-5 h-5 text-[#00BCD4]" />
                          <div>
                            <p className="text-sm text-gray-500">Telefon</p>
                            <p className="font-semibold">{order.customerPhone}</p>
                          </div>
                        </div>
                      </div>

                      {/* Delivery Type */}
                      <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
                        <Truck className="w-6 h-6 text-[#FFA726]" />
                        <div>
                          <p className="text-sm text-gray-500">Átvételi mód</p>
                          <p className="font-bold text-lg">{getDeliveryTypeLabel(order.deliveryType)}</p>
                        </div>
                      </div>

                      {/* Items */}
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3">Tételek:</h4>
                        <div className="space-y-2">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100">
                              <div>
                                <p className="font-semibold text-gray-900">{item.name}</p>
                                <p className="text-sm text-gray-500">{item.quantity}x {item.price} Ft</p>
                              </div>
                              <p className="font-bold text-[#FFA726]">
                                {item.quantity * item.price} Ft
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Total */}
                      <div className="flex justify-between items-center pt-4 border-t-2 border-gray-200">
                        <p className="text-xl font-bold text-gray-900">Végösszeg:</p>
                        <p className="text-3xl font-black text-[#FFA726]">
                          {order.total} Ft
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
